<template>
    <Card title="Warehouse Level Pricing">
        <div v-if="variant" class="location-pricing-page">
            <div class="page-actions">
                <Button noMargin type="button" @click="$router.push(`/products/${variant.parent || variant.id}`)">
                    <i class="fas fa-chevron-left" aria-hidden="true"></i>
                    Back to product details
                </Button>
            </div>

            <div class="pricing-layout">
                <aside class="scope-panel" aria-label="Pricing scope">
                    <label for="warehouse-pricing-scope">Pricing scope</label>
                    <select
                        id="warehouse-pricing-scope"
                        v-model="selectedWarehouseId"
                        :disabled="pricingProcessing"
                        data-test="warehouse-selector"
                    >
                        <option value="">Company level</option>
                        <option v-for="warehouse in warehouses" :key="warehouse.id" :value="warehouse.id">
                            {{ warehouse.name }}
                        </option>
                    </select>
                    <p>
                        {{
                            selectedWarehouseId
                                ? "Configure this product for one location."
                                : "Company pricing is the legacy fallback across locations."
                        }}
                    </p>
                </aside>

                <main class="pricing-main">
                    <div class="product-context">
                        <div class="product-context-label">Product</div>
                        <strong>{{ variant.name || variant.item_name || variant.item_code }}</strong>
                        <span>{{ variant.item_code }}</span>
                    </div>
                    <PricingForm
                        v-if="variant.item_code"
                        :warehouse="selectedWarehouseId || null"
                        :item_code="variant.item_code"
                        :variations="variants"
                        @processing-change="pricingProcessing = $event"
                    />
                    <div v-else class="empty-message">No variant code is available for pricing.</div>
                </main>
            </div>
        </div>

        <div v-else-if="loading" class="empty-message" role="status" aria-live="polite">
            Loading product pricing…
        </div>
        <div v-else-if="loadError" class="error-message" role="alert">{{ loadError }}</div>
        <div v-else class="empty-message">No pricing data found.</div>
    </Card>
</template>

<script>
import { mapState } from "vuex"
import { errMsg } from "../../utils/common"
import { http } from "../../utils/http"
import Button from "../UI/Button.vue"
import Card from "../UI/Card.vue"
import PricingForm from "./PricingForm.vue"

export default {
    name: "LocationLevelPricingEdit",
    components: { Card, Button, PricingForm },
    data() {
        return {
            variant: null,
            variants: [],
            selectedWarehouseId: "",
            pricingProcessing: false,
            loading: false,
            loadError: "",
        }
    },
    computed: {
        ...mapState("misc", ["warehouses"]),
    },
    async mounted() {
        this.loading = true
        try {
            await this.getItemData()
        } catch (error) {
            this.loadError = errMsg(error, "Unable to load the product")
        } finally {
            this.loading = false
        }
    },
    methods: {
        async getItemData() {
            const response = await http.get(`/items/${this.$route.params.id}`)
            this.variant = response.data.result
            this.variants = Array.isArray(response.data.variants) ? response.data.variants : []
        },
    },
}
</script>

<style lang="scss" scoped>
@use "sass:color";
@import "../../assets/variables.scss";
@import "../../assets/mixins.scss";

.location-pricing-page,
.pricing-main {
    min-width: 0;
}

.page-actions {
    margin-bottom: 14px;
}

.pricing-layout {
    display: grid;
    grid-template-columns: minmax(190px, 240px) minmax(0, 1fr);
    align-items: start;
    gap: 18px;
}

.scope-panel {
    position: sticky;
    top: $sectionGap;
    display: grid;
    gap: 7px;
    min-width: 0;
    padding: 13px;
    border: 1px solid $lightBorderColor;
    border-radius: 10px;
    background: $lightHoverColor;
}

.scope-panel label {
    margin: 0;
    font-size: 0.78rem;
    font-weight: 800;
    letter-spacing: 0.02em;
}

.scope-panel select {
    width: 100%;
    min-width: 0;
    min-height: 40px;
    padding: 7px 32px 7px 9px;
    border: 1px solid $lightBorderColor;
    border-radius: 6px;
    background: $lightCardBg;
    color: $textColor;
}

.scope-panel select:focus {
    border-color: $primaryColor;
    @include focus-ring;
}

.scope-panel p {
    margin: 0;
    color: rgba($textColor, 0.68);
    font-size: 0.78rem;
    line-height: 1.45;
}

.product-context {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 5px 9px;
    min-width: 0;
    margin-bottom: 14px;
    padding-bottom: 11px;
    border-bottom: 1px solid $lightBorderColor;
}

.product-context-label {
    width: 100%;
    color: rgba($textColor, 0.62);
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.07em;
    text-transform: uppercase;
}

.product-context strong,
.product-context span {
    min-width: 0;
    overflow-wrap: anywhere;
}

.product-context span {
    color: rgba($textColor, 0.65);
    font-size: 0.82rem;
}

.empty-message,
.error-message {
    padding: 14px;
    border-radius: 8px;
    background: $lightHoverColor;
}

.error-message {
    border: 1px solid rgba($dangerColor, 0.35);
    background: rgba($dangerColor, 0.08);
    color: color.adjust($dangerColor, $lightness: -8%);
}

:deep(.dark) .scope-panel,
:deep(.dark) .product-context {
    border-color: $darkBorderColor;
}

:deep(.dark) .scope-panel,
:deep(.dark) .empty-message {
    background: color.adjust($darkCardBg, $lightness: 4%);
    color: $darkTextColor;
}

:deep(.dark) .scope-panel select {
    border-color: $darkBorderColor;
    background: $darkCardBg;
    color: $darkTextColor;
}

:deep(.dark) .scope-panel p,
:deep(.dark) .product-context-label,
:deep(.dark) .product-context span {
    color: rgba($darkTextColor, 0.7);
}

@media (max-width: 767px) {
    .pricing-layout {
        grid-template-columns: minmax(0, 1fr);
        gap: 14px;
    }

    .scope-panel {
        position: static;
        grid-template-columns: minmax(0, 1fr);
        padding: 10px;
    }
}

@media (max-width: 430px) {
    .page-actions :deep(.app-btn) {
        width: 100%;
        min-width: 0;
    }
}
</style>
